import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { projectState, selectedFormTemplateTypeState } from 'state';
import { GeneralIds, RoutePaths } from 'types/types';
import { getRoutePath } from 'utils';

export function useBreadcrumbs() {
  const { selectedProject } = useRecoilValue(projectState);
  const formTemplateType = useRecoilValue(selectedFormTemplateTypeState);
  const { projectId, patientId, userId, formId, formTemplateTypeId } =
    useParams<GeneralIds>();
  const { t } = useTranslation();

  const urlParts = window.location.href.split('/');
  const dataType = urlParts[5]?.split('?')[0];
  const isEdit = window.location.href.includes('edit');
  const isView = window.location.href.includes('view');

  const breadcrumbs = {
    project: {
      label: selectedProject?.name || '',
      url: getRoutePath(RoutePaths.projectDashboard, { projectId }),
    },
    projects: {
      label: t('home.menu.projects'),
      url: getRoutePath(RoutePaths.projectSelector),
    },
    patients: {
      label: t('home.menu.patients'),
      url: getRoutePath(RoutePaths.patients, { projectId }),
    },
    users: {
      label: t('home.menu.users'),
      url: getRoutePath(RoutePaths.users, { projectId }),
    },
    patientVisits: {
      label: t('home.menu.patientVisits'),
      url: getRoutePath(RoutePaths.patientDetails, { projectId, patientId }),
    },
    patientEditor: {
      label: t('home.menu.patientData'),
      url: getRoutePath(RoutePaths.patientEditor, { projectId, patientId }),
    },
    user: {
      label: t('home.menu.userData'),
      url: getRoutePath(RoutePaths.userEditor, { projectId, userId }),
    },
    formTemplate: {
      label: t('home.menu.form'),
      url: getRoutePath(RoutePaths.formTemplateEditor, {
        projectId,
        formTemplateTypeId: formTemplateType?.id,
      }),
    },
    forms: {
      label: t('general.forms'),
      url: getRoutePath(RoutePaths.forms, { projectId, formId }),
    },
    export: {
      label: t('home.menu.export'),
      url: getRoutePath(RoutePaths.export, { projectId }),
    },
    reportTemplates: {
      label: t('home.menu.reports'),
      url: getRoutePath(RoutePaths.reportTemplates, { projectId }),
    },
  };

  const breadcrumbList = [breadcrumbs.projects];

  if (!projectId) {
    return breadcrumbList;
  } else {
    breadcrumbList.push(breadcrumbs.project);
  }

  if (dataType === 'export') {
    breadcrumbList.push(breadcrumbs.export);
  }

  if (dataType === 'patients') {
    breadcrumbList.push(breadcrumbs.patients);
  }

  if (dataType === 'users') {
    breadcrumbList.push(breadcrumbs.users);
  }

  if (dataType === 'formTemplate') {
    breadcrumbList.push(breadcrumbs.forms);
  }

  if (dataType === 'forms') {
    breadcrumbList.push(breadcrumbs.forms);
  }

  if (dataType === 'reports') {
    breadcrumbList.push(breadcrumbs.reportTemplates);
  }

  if (patientId) {
    if (isEdit || isView) {
      breadcrumbList.push(breadcrumbs.patientEditor);
    } else {
      breadcrumbList.push(breadcrumbs.patientVisits);
    }
  }

  if (userId) {
    breadcrumbList.push(breadcrumbs.user);
  }

  if (formTemplateTypeId && patientId) {
    breadcrumbList.push(breadcrumbs.formTemplate);
  }
  return breadcrumbList;
}
